import React, { useRef, useEffect } from 'react';
import { Table, theme } from 'antd';
import * as Styled from './CustomTable.styled';
import i18n from 'constants/translation/i18n';

const { useToken } = theme;

interface ICustomTableProps {
  drawTable: boolean;
  itemsCount: number;
  scheme?: string;
  [rest: string]: any;
}

const CustomTable: React.FC<ICustomTableProps> = ({
  drawTable,
  itemsCount,
  scheme,
  ...rest
}) => {
  const { token } = useToken();

  const tableRef = useRef<HTMLDivElement>(null);
  const isEmptyTable = !rest.dataSource || rest.dataSource.length === 0;

  const customPageSizeOptions = [1, 2, 5, 10, 20, 50, 100];

  useEffect(() => {
    const tableWrapper = tableRef.current;
    const topScroll = tableWrapper?.querySelector('.ant-table-wrapper')
      ?.previousElementSibling as HTMLElement;
    const bottomScroll = tableWrapper?.querySelector('.ant-table-wrapper')
      ?.nextElementSibling as HTMLElement;
    const tableContent = tableWrapper?.querySelector(
      '.ant-table-content'
    ) as HTMLElement;
    const scrollContent = topScroll?.querySelector('div') as HTMLElement;
    const bottomScrollContent = bottomScroll?.querySelector(
      'div'
    ) as HTMLElement;
    const table = tableContent?.querySelector('table') as HTMLElement;

    if (
      !topScroll ||
      !bottomScroll ||
      !tableContent ||
      !scrollContent ||
      !bottomScrollContent ||
      !table
    )
      return;

    const updateScrollWidth = () => {
      const width = `${table.scrollWidth}px`;
      scrollContent.style.width = width;
      bottomScrollContent.style.width = width;
    };

    const handleTopScroll = () => {
      tableContent.scrollLeft = topScroll.scrollLeft;
      bottomScroll.scrollLeft = topScroll.scrollLeft;
    };

    const handleBottomScroll = () => {
      tableContent.scrollLeft = bottomScroll.scrollLeft;
      topScroll.scrollLeft = bottomScroll.scrollLeft;
    };

    const handleTableScroll = () => {
      topScroll.scrollLeft = tableContent.scrollLeft;
      bottomScroll.scrollLeft = tableContent.scrollLeft;
    };

    // Enable body wheel scrolling for horizontal table navigation
    const handleBodyWheel = (e: WheelEvent) => {
      // Allow horizontal scrolling with regular mouse wheel when over table area
      if (Math.abs(e.deltaX) > 0 || e.shiftKey) {
        e.preventDefault();
        const scrollAmount = e.deltaX || e.deltaY;
        tableContent.scrollLeft += scrollAmount;
      }
    };

    // Make scroll bars always responsive to mouse position
    const makeScrollBarsAlwaysVisible = () => {
      topScroll.style.display = 'block';
      bottomScroll.style.display = 'block';
      topScroll.style.opacity = '1';
      bottomScroll.style.opacity = '1';
    };

    updateScrollWidth();
    makeScrollBarsAlwaysVisible();

    const resizeObserver = new ResizeObserver(() => {
      updateScrollWidth();
      makeScrollBarsAlwaysVisible();
    });
    resizeObserver.observe(table);

    topScroll.addEventListener('scroll', handleTopScroll);
    bottomScroll.addEventListener('scroll', handleBottomScroll);
    tableContent.addEventListener('scroll', handleTableScroll);
    document.body.addEventListener('wheel', handleBodyWheel, {
      passive: false
    });

    return () => {
      resizeObserver.disconnect();
      topScroll.removeEventListener('scroll', handleTopScroll);
      bottomScroll.removeEventListener('scroll', handleBottomScroll);
      tableContent.removeEventListener('scroll', handleTableScroll);
      document.body.removeEventListener('wheel', handleBodyWheel);
    };
  }, [rest.dataSource, rest.columns]);

  return (
    <Styled.TableWrapper
      ref={tableRef}
      token={token}
      drawTable={drawTable}
      scheme={scheme}
      isEmpty={isEmptyTable}
    >
      <Styled.TopScrollbar>
        <Styled.ScrollContent />
      </Styled.TopScrollbar>
      <Table
        {...rest}
        pagination={
          drawTable
            ? {
                defaultPageSize: 2,
                hideOnSinglePage: false,
                showSizeChanger: true,
                pageSizeOptions: customPageSizeOptions.map((size) => `${size}`),
                position: ['topLeft'],
                showQuickJumper: true,
                total: itemsCount,
                locale: {
                  items_per_page: `/ ${i18n.t('PAGE')}`,
                  jump_to: i18n.t('GO_TO_PAGE'),
                  page: i18n.t('PAGE')
                },
                showTotal: (total: number, range: number[]) => {
                  return `${range[0]}-${range[1]} ${i18n.t(
                    'OF'
                  )} ${total} ${i18n.t('ITEMS')}`;
                },
                ...rest.pagination
              }
            : false
        }
      />
      <Styled.BottomScrollbar>
        <Styled.ScrollContent />
      </Styled.BottomScrollbar>
    </Styled.TableWrapper>
  );
};

export default CustomTable;
